import {useEffect, useCallback, useState, useMemo, useRef} from 'react'
import {useLocation} from 'react-router-dom'
import {useTranslation} from 'react-i18next'

import {Modal} from '../../_metronic/elements/Modal'
import {KTSVG} from '../../_metronic/helpers'
import {usePayment} from './Payment'
import {useErrorHandler} from '../../_metronic/helpers/ErrorHandler'
import * as UserService from '../modules/user/services/UserService'
import {useAuth} from '../modules/auth'
import {useIsMobile} from '../shared/responsive'
import './PaymentModal.scss'

const SALES_LEAD_STORAGE_KEY = 'specific-payment-required-leads'
const SALES_WHATSAPP_NUMBER = '5493584925325'

const RESOURCE_CONFIGS = [
    {
        id: 'reports_ndvi_ha',
        aliases: ['reports_ndvi_ha', 'reports_ha'],
        defaultLabel: 'Hectáreas para reportes',
        nameKey: 'payments:view.names.reports_ndvi',
        titleKey: 'payments:modal.resources.reports.title',
        descriptionKey: 'payments:modal.resources.reports.description',
        actionKey: 'payments:modal.resources.reports.action',
        contactKey: 'payments:modal.resources.reports.contact',
        variant: 'reports',
    },
    {
        id: 'planet_hd_ha',
        aliases: ['planet_hd_ha'],
        defaultLabel: 'Hectáreas de Planet HD',
        nameKey: 'payments:view.names.planet_hd',
        titleKey: 'payments:modal.resources.planet_hd.title',
        descriptionKey: 'payments:modal.resources.planet_hd.description',
        actionKey: 'payments:modal.resources.planet_hd.action',
        contactKey: 'payments:modal.resources.planet_hd.contact',
        variant: 'reports',
    },
    {
        id: 'ambients',
        aliases: ['ambients'],
        defaultLabel: 'Hectáreas para ambientar',
        nameKey: 'payments:view.names.ambients',
        titleKey: 'payments:modal.resources.ambients.title',
        actionKey: 'payments:modal.resources.ambients.action',
        contactKey: 'payments:modal.resources.ambients.contact',
        variant: 'ambients',
    },
]

const getSessionStorage = () => {
    if (typeof window === 'undefined') {
        return null
    }

    try {
        return window.sessionStorage || null
    } catch (error) {
        return null
    }
}

const getPaymentData = (payment) => payment?.data || {}

const getPaymentCode = (payment) => payment?.code || getPaymentData(payment)?.payment_code || getPaymentData(payment)?.code || ''

const getPaymentResourceId = (payment) => (
    payment?.resourceId ||
    payment?.resource_id ||
    getPaymentData(payment)?.resource_id ||
    ''
)

const getResourceConfig = (payment, pathname) => {
    const resourceNeedle = `${getPaymentResourceId(payment)} ${getPaymentCode(payment)}`.toLowerCase()

    if (resourceNeedle.includes('report')) {
        return RESOURCE_CONFIGS[0]
    }

    if (resourceNeedle.includes('planet') || resourceNeedle.includes('hd')) {
        return RESOURCE_CONFIGS[1]
    }

    if (resourceNeedle.includes('ambient')) {
        return RESOURCE_CONFIGS[2]
    }

    if ((pathname || '').includes('/reports')) {
        return RESOURCE_CONFIGS[0]
    }

    return RESOURCE_CONFIGS[2]
}

const getNumericValue = (values = {}, aliases = []) => {
    for (const alias of aliases) {
        const value = Number(values[alias])

        if (Number.isFinite(value)) {
            return value
        }
    }

    return 0
}

const getOptionalNumericValue = (values = {}, aliases = []) => {
    for (const alias of aliases) {
        if (!Object.prototype.hasOwnProperty.call(values, alias)) {
            continue
        }

        const value = Number(values[alias])
        if (Number.isFinite(value)) {
            return value
        }
    }

    return null
}

const formatHectares = (value, language) => {
    const numberLocale = language?.startsWith('es') ? 'es-AR' : 'en-US'

    return new Intl.NumberFormat(numberLocale, {
        maximumFractionDigits: 2,
    }).format(Number.isFinite(value) ? Math.max(value, 0) : 0)
}

const getResourceBalance = (plan, resourceConfig) => {
    if (!plan || !resourceConfig) {
        return null
    }

    const quantity = getNumericValue(plan.plan, resourceConfig.aliases)
    const usage = getNumericValue(plan.usage, resourceConfig.aliases)

    return parseInt(quantity - usage)
}

const ReportResourceIcon = () => (
    <svg
        aria-hidden="true"
        className="payment-resource-card__report-icon"
        focusable="false"
        viewBox="0 0 96 96"
    >
        <path d="M26 12h31l17 17v55H26z" />
        <path d="M57 12v18h17" />
        <path d="M38 66V52M49 66V43M60 66V36" />
        <path d="M36 73h28" />
    </svg>
)

const ResourcePlanCard = ({available, label, variant}) => {
    const {t, i18n} = useTranslation()
    const formattedAvailable = formatHectares(available, i18n.language)
    const isReport = variant === 'reports'

    return (
        <div className={`payment-resource-card payment-resource-card--${variant}`}>
            <div className="payment-resource-card__summary">
                <div className="payment-resource-card__heading">
                    <span className="payment-resource-card__eyebrow">
                        {t('payments:modal.available_balance')}
                    </span>
                    <h3 className="payment-resource-card__title">{label}</h3>
                </div>
                <div className="payment-resource-card__balance">
                    <strong>{formattedAvailable}</strong>
                    <span>{t('payments:modal.hectares')}</span>
                </div>
            </div>
            <div className="payment-resource-card__visual" aria-hidden="true">
                {isReport ? (
                    <ReportResourceIcon />
                ) : (
                    <img className="mw-100 mh-300px card-rounded-bottom" alt="" src="data:image/svg+xml;base64,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"/>
                )}
            </div>
        </div>
    )
}

const PaymentModal = () => {
    const {t, i18n} = useTranslation();
    const {currentUser} = useAuth();
    const {errorHandler} = useErrorHandler();
    const {active, payment, setActive} = usePayment();
    const location = useLocation();
    const isMobile = useIsMobile();
    const [plan, setPlan] = useState(null);
    const notifiedSalesLeads = useRef(new Set());
    const resourceConfig = useMemo(
        () => getResourceConfig(payment, location.pathname),
        [payment, location.pathname]
    );
    const email = useMemo(() => {
        let d = "ventas@specific-ag.com";
        if (window.tenant === "natalseeds.app") {
            d = "celeste.gardenes@natalseeds.com";
        }

        return d;
    }, [])
    const resourceLabel = useMemo(
        () => t(resourceConfig.nameKey, {defaultValue: resourceConfig.defaultLabel}),
        [resourceConfig, t]
    );
    const available = useMemo(() => {
        if (resourceConfig.id === 'planet_hd_ha') {
            const preflightBalance = getOptionalNumericValue(getPaymentData(payment), [
                'available_hd_ha',
                'available_ha',
                'remaining_hd_ha',
                'remaining_ha',
            ])
            if (preflightBalance != null) {
                return preflightBalance
            }
        }

        return getResourceBalance(plan, resourceConfig)
    }, [payment, plan, resourceConfig]);
    const formattedAvailable = useMemo(
        () => formatHectares(available, i18n.language),
        [available, i18n.language]
    );
    const actionText = t(resourceConfig.actionKey);
    const contactResource = t(resourceConfig.contactKey);
    const whatsappMessage = t('payments:modal.whatsapp_message', {
        resource: contactResource,
    });
    const whatsappHref = `https://wa.me/${SALES_WHATSAPP_NUMBER}?text=${encodeURIComponent(whatsappMessage)}`;
    const emailSubject = t('payments:modal.email_subject', {
        resource: contactResource,
    });
    const emailHref = `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(email)}&su=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(whatsappMessage)}`;


    const load = useCallback(async () => {
        if (!active || !currentUser) {
            return;
        }

        const resPlan = await errorHandler((UserService.currentPlan()));
        const resUsage = await errorHandler((UserService.companyUsage()));
        
        if (resPlan.ok && resUsage.ok) {
            const p = resPlan.data.data;
            const totalPlan = {};
            for (const planIndex in p.resources) {
                totalPlan[p.resources[planIndex].resource_id] = p.resources[planIndex].quantity;
            }

            const u = resUsage.data.data;
            const totalUsage = {};
            for (const usageIndex in u.usages) {
                totalUsage[u.usages[usageIndex].resource] = u.usages[usageIndex].usage;
            }

            setPlan({
                plan: totalPlan,
                usage: totalUsage
            });
        }
    }, [active, currentUser]);

    useEffect(() => {
        load();
    }, [currentUser, active]);

    useEffect(() => {
        if (!active || !currentUser || !resourceConfig || available == null) {
            return;
        }

        const storage = getSessionStorage();
        const paymentData = getPaymentData(payment);
        const code = getPaymentCode(payment);
        const resourceId = getPaymentResourceId(payment) || resourceConfig.id;
        const leadKey = [
            currentUser.id || currentUser.user_id || currentUser.email || 'anonymous',
            resourceId,
            code || 'need_payment',
            paymentData.required_ha || paymentData.requiredHa || '',
            paymentData.missing_ha || paymentData.missingHa || '',
            location.pathname,
        ].join(':');

        if (
            notifiedSalesLeads.current.has(leadKey) ||
            storage?.getItem(`${SALES_LEAD_STORAGE_KEY}:${leadKey}`)
        ) {
            return;
        }

        notifiedSalesLeads.current.add(leadKey);
        storage?.setItem(`${SALES_LEAD_STORAGE_KEY}:${leadKey}`, '1');

        UserService.notifyPaymentRequiredLead({
            code,
            resource_id: resourceId,
            resource_label: resourceLabel,
            operation: payment?.operation || paymentData.operation || null,
            required_ha: paymentData.required_ha || paymentData.requiredHa || null,
            available_ha: paymentData.available_ha ?? paymentData.availableHa ?? available,
            missing_ha: paymentData.missing_ha || paymentData.missingHa || null,
            tenant: typeof window !== 'undefined' ? window.tenant : null,
            path: location.pathname,
            url: typeof window !== 'undefined' ? window.location.href : null,
            user: {
                id: currentUser.id || currentUser.user_id || null,
                company_id: currentUser.company_id || null,
                email: currentUser.email || null,
                fullname: currentUser.fullname || `${currentUser.first_name || ''} ${currentUser.last_name || ''}`.trim(),
            },
        }).catch(() => {});
    }, [active, available, currentUser, location.pathname, payment, resourceConfig, resourceLabel]);

    if (plan == null){
        return <></>;
    }

    return (
        <Modal
            open={active}
            setOpen={setActive}
            className="payment-modal-layer"
            backdropClassName="payment-modal-backdrop"
        >
            <div className="modal-dialog modal-dialog-centered payment-modal-dialog">
                <div className={`modal-content payment-modal-content ${isMobile ? 'payment-modal-content--mobile' : ''}`.trim()}>
                    <div className="modal-header payment-modal-header">
                        <h5 className="modal-title payment-modal-title">
                            {t(resourceConfig.titleKey)}
                        </h5>
                        <button
                            type="button"
                            className="btn btn-icon btn-sm btn-active-light-primary payment-modal-header-close"
                            onClick={() => setActive(false)}
                            aria-label={t('payments:modal.close')}
                        >
                            <span aria-hidden="true">&times;</span>
                        </button>
                    </div>
                    <div className="modal-body payment-modal-body">
                        <div className="payment-modal-copy">
                            <p className="payment-modal-description">
                                {resourceConfig.descriptionKey
                                    ? t(resourceConfig.descriptionKey, {available: formattedAvailable})
                                    : t('payments:modal.available_copy', {
                                        available: formattedAvailable,
                                        action: actionText,
                                    })}
                            </p>
                            <p className="payment-modal-contact-copy">
                                {t('payments:modal.contact_copy', {email})}
                            </p>
                            <div className="payment-modal-actions">
                                <a
                                    href={whatsappHref}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-primary payment-modal-cta"
                                >
                                    <KTSVG
                                        path="/media/icons/duotune/communication/com012.svg"
                                        className="payment-modal-action-icon"
                                        svgClassName="payment-modal-action-svg"
                                    />
                                    {t('payments:modal.buy')}
                                </a>
                                <a
                                    href={emailHref}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-light-primary payment-modal-email"
                                >
                                    <KTSVG
                                        path="/media/icons/duotune/communication/com002.svg"
                                        className="payment-modal-action-icon"
                                        svgClassName="payment-modal-action-svg"
                                    />
                                    {t('payments:modal.email')}
                                </a>
                            </div>
                        </div>
                        <div className="payment-modal-plan">
                            <h3 className="payment-modal-plan-title">
                                {t('payments:modal.current_plan')}
                            </h3>
                            <div className="payment-modal-plan-shell">
                                <ResourcePlanCard
                                    available={available}
                                    label={resourceLabel}
                                    variant={resourceConfig.variant}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="modal-footer payment-modal-footer">
                        <button
                            type="button"
                            className="btn btn-light payment-modal-close"
                            onClick={() => setActive(false)}
                        >
                            {t('payments:modal.close')}
                        </button>
                    </div>
                </div>
            </div>
        </Modal>
    )
};

export default PaymentModal;
